import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchProducts } from "@/lib/api";
import MotifCircle from "@/components/site/MotifCircle";
import { MOTIF_LIST } from "@/lib/motifs";
import { Butterfly } from "@/components/site/LogoMark";
import CollectionHerbarium from "@/components/site/CollectionHerbarium";
import TwinningBand from "@/components/site/TwinningBand";
import GrownupsBand from "@/components/site/GrownupsBand";

/**
 * Home — Option 2 (the current live hero, clean uploaded image).
 * Preserved so the founder can switch back to it at any time via /home-option-2
 * or by pointing App.js "/" to this component.
 */
const HomeOptionTwo = () => {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    fetchProducts({ category: "girls" }).then((all) => setFeatured(all.slice(0, 6))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page-option-2">
      {/* Spacer for the fixed cream header so the hero image sits cleanly below it */}
      <div className="h-[88px] md:h-[108px]" aria-hidden="true" />

      {/* HERO — clean uploaded editorial image, cream header sits above */}
      <section
        className="relative w-full overflow-hidden"
        data-testid="hero"
      >
        <Link to="/product/butterfly-bliss-pink" className="block" data-testid="hero-link">
          <img
            src="/api/uploads/home/hero-mockup.webp"
            alt="Butterfly Bliss Pink — on the plinth in the Colonnade"
            className="w-full h-auto block"
            data-testid="hero-headline"
          />
        </Link>
      </section>

      {/* SHOP BY MOTIF */}
      <section className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 py-32" data-testid="motifs-section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-20">
          <div className="md:col-span-4">
            <div className="nav-item mb-4">The signature</div>
            <h2 className="serif text-4xl md:text-5xl leading-tight">
              Shop by <em className="italic">motif</em>.
            </h2>
          </div>
          <p className="md:col-span-6 md:col-start-7 text-ink/80 text-base leading-relaxed self-end">
            Every piece is built around a single hand-embroidered figure. Choose the one your
            small human will not put down.
          </p>
        </div>
        <div className="grid grid-cols-4 md:grid-cols-8 gap-y-12 gap-x-4">
          {MOTIF_LIST.map((m) => <MotifCircle key={m.key} motifKey={m.key} label={m.label} />)}
        </div>
      </section>

      {/* Featured — Herbarium Album */}
      <section className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 py-24 md:py-32" data-testid="featured-section">
        <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-end mb-16 md:mb-20">
          <div className="md:col-span-6">
            <div className="nav-item mb-3">The girls&rsquo; collection</div>
            <h2 className="serif text-4xl md:text-5xl leading-tight">
              First of the <em className="italic">season</em>.
            </h2>
          </div>
          <p className="md:col-span-4 md:col-start-8 text-ink/70 text-sm leading-relaxed">
            Six pieces catalogued like specimens in a field album. Each entry is dated, priced, and tagged with its motif. Open the entry to walk the piece.
          </p>
        </div>
        <CollectionHerbarium items={featured} />
        <div className="mt-16 flex justify-center">
          <Link to="/shop" className="crimson-link nav-item !text-[12px]" data-testid="featured-view-all">
            View the whole collection →
          </Link>
        </div>
      </section>

      {/* Editorial quote */}
      <section className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 py-32">
        <div className="grid md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-2 flex md:justify-end">
            <Butterfly size={40} />
          </div>
          <blockquote className="md:col-span-8 serif text-3xl md:text-5xl leading-tight text-ink" data-testid="editorial-quote">
            &ldquo;Eleven hours. That&rsquo;s what one butterfly takes. We time it because it matters,
            not because we&rsquo;re in a hurry.&rdquo;
          </blockquote>
          <div className="md:col-span-2 nav-item text-ink/70">— Meghana &amp; Lallitha</div>
        </div>
      </section>

      <GrownupsBand />
      <TwinningBand />
    </div>
  );
};

export default HomeOptionTwo;
